// alertService.js
import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';

const ALERT_DURATION_MS = 3500;
const ACCENT = '#1b4d2e';

// Per-severity styling — tuned to the Venueboard palette
// (brand green for success, muted tints for the rest).
const SEVERITY = {
  success: {
    Icon: CheckCircleIcon,
    accent: ACCENT,
    iconBg: 'bg-[#1b4d2e]/10 dark:bg-[#1b4d2e]/25',
    iconColor: 'text-[#1b4d2e] dark:text-emerald-300',
  },
  error: {
    Icon: XCircleIcon,
    accent: '#dc2626',
    iconBg: 'bg-red-50 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
  },
  warning: {
    Icon: ExclamationTriangleIcon,
    accent: '#d97706',
    iconBg: 'bg-amber-50 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  info: {
    Icon: InformationCircleIcon,
    accent: '#3b82f6',
    iconBg: 'bg-blue-50 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
};

const Alert = ({ id, message, severity = 'info', onClose }) => {
  const cfg = SEVERITY[severity] || SEVERITY.info;
  const Icon = cfg.Icon;

  useEffect(() => {
    const timer = setTimeout(() => onClose(id), ALERT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [id, onClose]);

  return (
    <div
      className="venueboard-alert relative flex items-start gap-3 w-80 max-w-[calc(100vw-2rem)] pl-4 pr-3 py-3 rounded-xl border border-l-4 border-zinc-200/80 dark:border-zinc-700/60 bg-white dark:bg-zinc-800 shadow-lg overflow-hidden"
      style={{ borderLeftColor: cfg.accent }}
      role="status"
    >
      <div className={`shrink-0 mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center ${cfg.iconBg}`}>
        <Icon className={`h-5 w-5 ${cfg.iconColor}`} />
      </div>
      <span className="flex-1 text-sm leading-snug text-zinc-700 dark:text-zinc-200 pt-1">
        {message}
      </span>
      <button
        type="button"
        onClick={() => onClose(id)}
        className="shrink-0 p-1 -mr-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:text-zinc-200 dark:hover:bg-zinc-700 transition-colors"
        aria-label="Cerrar notificación"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
      {/* Time-remaining progress bar */}
      <div
        className="venueboard-alert-progress absolute bottom-0 left-0 h-0.5"
        style={{ background: cfg.accent }}
      />
    </div>
  );
};

let addAlertCallback = null;

export const showAlert = (message, severity = 'info') => {
  if (addAlertCallback) {
    addAlertCallback({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      message,
      severity,
    });
  }
};

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);

  const addAlert = useCallback((alert) => {
    setAlerts((prev) => [...prev, alert]);
  }, []);

  useEffect(() => {
    addAlertCallback = addAlert;
    return () => {
      addAlertCallback = null;
    };
  }, [addAlert]);

  const removeAlert = (id) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  return (
    <>
      {children}
      {ReactDOM.createPortal(
        <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
          {alerts.map((alert) => (
            <div key={alert.id} className="pointer-events-auto">
              <Alert {...alert} onClose={removeAlert} />
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};
